import { VulnerabilityAssessmentResult } from './../../interfaces/VulnerabilityAssessmentResult';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { throwError, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/auth/auth.service';
import { CompanyUser } from '../../interfaces/CompanyUser';
import { CompanyUserResponse } from '../../interfaces/CompanyUserResponse';
import { InternalUserResponse } from '../../interfaces/InternalUserResponse';
import { InternalUser } from '../../interfaces/InternalUser';
import { RoleResponse } from '../../interfaces/RoleResponse';
import { Role } from '../../interfaces/Role';
import { CompanyResponse } from '../../interfaces/CompanyResponse';
import { Company } from '../../interfaces/Company';
import { VulnerabilityAssessment } from '../../interfaces/VulnerabilityAssessment';
import { VulnerabilityAssessmentResponse } from '../../interfaces/VulnerabilityAssessmentResponse';
import { Brand } from '../../interfaces/Brand';
import { BrandResponse } from '../../interfaces/BrandResponse';
import { BranchOffice } from '../../interfaces/BranchOffice';
import { BranchOfficeResponse } from '../../interfaces/BranchOfficeResponse';
import { ContactMediumTypeResponse } from '../../interfaces/ContactMediumTypeResponse';
import { SeverityResponse } from '../../interfaces/SeverityResponse';
import { VulnerabilityToolResponse } from '../../interfaces/VulnerabilityToolResponse';
import { VulnerabilityOriginResponse } from '../../interfaces/VulnerabilityOriginResponse';
import { Asset } from '../../interfaces/Asset';
import { AssetResponse } from '../../interfaces/AssetResponse';
import { AssetVerificationStatusResponse } from '../../interfaces/AssetVerificationStatusResponse';
import { VulnerabilityAssessmentResultResponse } from '../../interfaces/VulnerabilityAssessmentResultResponse';
import { StateResponse } from '../../interfaces/StateResponse';
import { EvidenceResponse } from '../../interfaces/EvidenceResponse';
import { HelpRequest } from '../../interfaces/HelpRequest';
import { VulnerabilityTrackingResponse } from '../../interfaces/VulnerabilityTrackingResponse';
import { VulnerabilityTracking } from '../../interfaces/VulnerabilityTracking';
import { DashboardResponse } from '../../interfaces/DashboardResponse';
import { VulnerabilityManagerResponse } from '../../interfaces/VulnerabilityManagerResponse';
import { GlobalConstants } from '../../common/global-constants';
import { AuthenticationResponse } from '../../interfaces/AuthenticationResponse';

@Injectable({
    providedIn: 'root',
})
export class ApiService {
    private _url: string = environment.apiUrlBase;
    private _company: string = '/v1/company';
    private _vulnerability_assessment: string = '/v1/vulnerabilityAssessment';
    private _vulnerability_assessment_result: string =
        '/v1/vulnerabilityAssessmentResult';
    private _vulnerability_tracking: string = '/v1/vulnerabilityTracking';
    private _role: string = '/v1/role';
    private _internal_user: string = '/v1/internalUser';
    private _brand: string = '/v1/brand';
    private _branch_office: string = '/v1/branchOffice';
    private _asset: string = '/v1/asset';
    private _vulnerability_tool: string = '/v1/vulnerabilityTool';
    private _vulnerability_origin: string = '/v1/vulnerabilityOrigin';
    private _state: string = '/v1/state';
    private _evidence: string = '/v1/evidence';
    private _severity: string = '/v1/severity';
    private _severity_level: string = '/v1/severityLevel';
    private _resolver: string = '/v1/resolver';
    private _company_user: string = '/v1/companyUser';
    private _contact_medium_type: string = '/v1/contactMediumType';
    private _asset_verification_status: string = '/v1/assetVerificationStatus';
    private _health: string = '/health';
    private _dashboard: string = '/v1/dashboard';
    private _vulnerability_manager: string = '/v1/vulnerabilityManager';
    private _vulnerability_report: string =
        '/v1/vulnerabilityReport/resolutiontime';
    private _kill: string = '/kill';

    constructor(
        private _httpClient: HttpClient,
        private _authService: AuthService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------
    // evidence-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * getEvidenceById
     */
    getEvidenceById(
        id: string,
        companyUser: boolean = false
    ): Observable<EvidenceResponse> {
        let path =
            `${this._url}${this._evidence}/${id}` +
            (companyUser ? '/companyUser' : '');
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<EvidenceResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getEvidenceByVulnerabilityResult
     */
    getEvidenceByVulnerabilityResult(
        vulnerabilityResultId: string,
        page: Number,
        limit: Number,
        sortBy: string,
        sortOrder: string,
        companyUser: boolean = false
    ): Observable<EvidenceResponse> {
        let path =
            `${this._url}${this._evidence}/vulnerabilityResult/${vulnerabilityResultId}` +
            (companyUser ? '/companyUser' : '') +
            `?page=${page}&limit=${limit}`; //&sortBy=${sortBy}&sortOrder=${sortOrder}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<EvidenceResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * addEvidence
     */
    addEvidence(evidence: FormData): Observable<EvidenceResponse> {
        let path = `${this._url}${this._evidence}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .post<EvidenceResponse>(path, evidence, {
                headers,
            })
            .pipe(catchError(this.handleError));
    }

    /**
     * editEvidence
     */
    editEvidence(id: string, evidence: FormData): Observable<EvidenceResponse> {
        let path = `${this._url}${this._evidence}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .patch<EvidenceResponse>(path, evidence, {
                headers,
            })
            .pipe(catchError(this.handleError));
    }

    /**
     * deleteEvidence
     */
    deleteEvidence(id: string): Observable<EvidenceResponse> {
        let path = `${this._url}${this._evidence}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .delete<EvidenceResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // company-user-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * getCompanyUser
     */
    getCompanyUser(
        page: Number,
        limit: Number,
        sortBy: string,
        sortOrder: string
    ): Observable<CompanyUserResponse> {
        let path = `${this._url}${this._company_user}?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<CompanyUserResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getCompanyUserByIdAndCompanyId
     */
    getCompanyUserByIdAndCompanyId(
        id: string,
        companyId: string
    ): Observable<CompanyUserResponse> {
        let path = `${this._url}${this._company_user}/${id}/company/${companyId}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<CompanyUserResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getCompanyUserByCompanyId
     */
    getCompanyUserByCompanyId(
        companyId: string,
        page: Number,
        limit: Number,
        sortBy: string,
        sortOrder: string
    ): Observable<CompanyUserResponse> {
        let path = `${this._url}${this._company_user}/company/${companyId}?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<CompanyUserResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getCompanyUserByBranchOfficeId
     */
    getCompanyUserByBranchOfficeId(
        branchOfficeId: string,
        page: Number,
        limit: Number,
        sortBy: string,
        sortOrder: string
    ): Observable<CompanyUserResponse> {
        let path = `${this._url}${this._company_user}/branchOffice/${branchOfficeId}?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<CompanyUserResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * addCompanyUser
     */
    addCompanyUser(companyUser: CompanyUser): Observable<CompanyUserResponse> {
        let path = `${this._url}${this._company_user}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .post<CompanyUserResponse>(path, companyUser, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * patchCompanyUser
     */
    patchCompanyUser(
        id: string,
        companyUser: CompanyUser
    ): Observable<CompanyUserResponse> {
        let path = `${this._url}${this._company_user}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .patch<CompanyUserResponse>(path, companyUser, {
                headers,
            })
            .pipe(catchError(this.handleError));
    }

    /**
     * deleteCompanyUser
     */
    deleteCompanyUser(id: string): Observable<CompanyUserResponse> {
        let path = `${this._url}${this._company_user}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .delete<CompanyUserResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // company-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * getCompanyById
     */
    getCompanyById(id: string): Observable<CompanyResponse> {
        let path = `${this._url}${this._company}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<CompanyResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * updateCompany
     */
    updateCompany(id: string, company: Company): Observable<CompanyResponse> {
        let path = `${this._url}${this._company}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .put<CompanyResponse>(path, company, {
                headers,
            })
            .pipe(catchError(this.handleError));
    }

    /**
     * deleteCompany
     */
    deleteCompany(id: string): Observable<CompanyResponse> {
        let path = `${this._url}${this._company}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .delete<CompanyResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * patchCompany
     */
    patchCompany(id: string, company: Company): Observable<CompanyResponse> {
        let path = `${this._url}${this._company}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .patch<CompanyResponse>(path, company, {
                headers,
            })
            .pipe(catchError(this.handleError));
    }

    /**
     * getCompanyByCompanyUser
     */
    getCompanyByCompanyUser(): Observable<CompanyResponse> {
        let path = `${this._url}${this._company}/companyUser`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<CompanyResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * addCompany
     */
    addCompany(company: Company): Observable<CompanyResponse> {
        let path = `${this._url}${this._company}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .post<CompanyResponse>(path, company, {
                headers,
            })
            .pipe(catchError(this.handleError));
    }

    /**
     * getCompanyPagination
     */
    getCompanyPagination(
        page: Number,
        limit: Number,
        sortBy: string,
        sortOrder: string
    ): Observable<CompanyResponse> {
        let path = `${this._url}${this._company}/pagination?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
        console.log(path);
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<CompanyResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // vulnerability-tracking-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * getVulnerabilityTrackingById
     */
    getVulnerabilityTrackingById(
        id: string
    ): Observable<VulnerabilityTrackingResponse> {
        let path = `${this._url}${this._vulnerability_tracking}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<VulnerabilityTrackingResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getVulnerabilityTrackingByVulnerabilityResult
     */
    getVulnerabilityTrackingByVulnerabilityResult(
        vulnerabilityResultId: string,
        page: Number,
        limit: Number,
        sortBy: string,
        sortOrder: string
    ): Observable<VulnerabilityTrackingResponse> {
        let path = `${this._url}${this._vulnerability_tracking}/vulnerabilityResult/${vulnerabilityResultId}?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<VulnerabilityTrackingResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * addVulnerabilityTracking
     */
    addVulnerabilityTracking(
        vulnerabilityTracking: FormData
    ): Observable<VulnerabilityTrackingResponse> {
        let path = `${this._url}${this._vulnerability_tracking}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .post<VulnerabilityTrackingResponse>(path, vulnerabilityTracking, {
                headers,
            })
            .pipe(catchError(this.handleError));
    }

    /**
     * patchVulnerabilityTracking
     */
    patchVulnerabilityTracking(
        id: string,
        vulnerabilityTracking: VulnerabilityTracking
    ): Observable<VulnerabilityTrackingResponse> {
        let path = `${this._url}${this._vulnerability_tracking}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .patch<VulnerabilityTrackingResponse>(path, vulnerabilityTracking, {
                headers,
            })
            .pipe(catchError(this.handleError));
    }

    /**
     * deleteVulnerabilityTracking
     */
    deleteVulnerabilityTracking(
        id: string
    ): Observable<VulnerabilityTrackingResponse> {
        let path = `${this._url}${this._vulnerability_tracking}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .delete<VulnerabilityTrackingResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // vulnerability-assessment-result-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * getVulnerabilityAssessmentResultByAssessmentId
     */
    getVulnerabilityAssessmentResultByAssesmentId(
        vulnerabilityAssessmentId: string,
        page: Number,
        limit: Number,
        sortBy: string,
        sortOrder: string,
        internalUser: boolean = true
    ): Observable<VulnerabilityAssessmentResultResponse> {
        let path =
            `${this._url}${this._vulnerability_assessment_result}/vulnerabilityAssessment/${vulnerabilityAssessmentId}` +
            (internalUser ? '' : '/user') +
            `?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<VulnerabilityAssessmentResultResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getVulnerabilityAssessmentResultById
     */
    getVulnerabilityAssessmentResultById(
        id: string,
        internalUser: boolean = true
    ): Observable<VulnerabilityAssessmentResultResponse> {
        let path =
            `${this._url}${this._vulnerability_assessment_result}/${id}` +
            (internalUser ? '' : '/user');
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<VulnerabilityAssessmentResultResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * postVulnerabilityAssessmentResultHelp
     */
    postVulnerabilityAssessmentResultHelp(
        vulnerabilityResultId: HelpRequest
    ): Observable<VulnerabilityAssessmentResultResponse> {
        let path = `${this._url}${this._vulnerability_assessment_result}/help`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .post<VulnerabilityAssessmentResultResponse>(
                path,
                vulnerabilityResultId,
                {
                    headers,
                }
            )
            .pipe(catchError(this.handleError));
    }

    /**
     * addVulnerabilityAssessmentResult
     */
    addVulnerabilityAssessmentResult(
        vulnerabilityAssessmentResult: FormData
    ): Observable<VulnerabilityAssessmentResultResponse> {
        let path = `${this._url}${this._vulnerability_assessment_result}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .post<VulnerabilityAssessmentResultResponse>(
                path,
                vulnerabilityAssessmentResult,
                {
                    headers,
                }
            )
            .pipe(catchError(this.handleError));
    }

    /**
     * patchVulnerabilityAssessmentResult
     */
    patchVulnerabilityAssessmentResult(
        id: string,
        vulnerabilityAssessmentResult: VulnerabilityAssessmentResult
    ): Observable<VulnerabilityAssessmentResultResponse> {
        let path = `${this._url}${this._vulnerability_assessment_result}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .patch<VulnerabilityAssessmentResultResponse>(
                path,
                vulnerabilityAssessmentResult,
                {
                    headers,
                }
            )
            .pipe(catchError(this.handleError));
    }

    /**
     * putVulnerabilityAssessmentResultState
     */
    putVulnerabilityAssessmentResultState(
        changeStatus: string
    ): Observable<VulnerabilityAssessmentResultResponse> {
        let path = `${this._url}${this._vulnerability_assessment_result}/state`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
            'content-type': 'application/json',
        });
        return this._httpClient
            .put<VulnerabilityAssessmentResultResponse>(path, changeStatus, {
                headers,
            })
            .pipe(catchError(this.handleError));
    }

    /**
     * deleteVulnerabilityAssessmentResult
     */
    deleteVulnerabilityAssessmentResult(
        id: string
    ): Observable<VulnerabilityAssessmentResultResponse> {
        let path = `${this._url}${this._vulnerability_assessment_result}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .delete<VulnerabilityAssessmentResultResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * patchVulnerabilityAssessmentResultAssignUser
     */
    patchVulnerabilityAssessmentResultAssignUser(
        id: string,
        idUser: string
    ): Observable<VulnerabilityAssessmentResultResponse> {
        let path = `${this._url}${this._vulnerability_assessment_result}/${id}/assignUser`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
            'content-type': 'application/json',
        });
        return this._httpClient
            .patch<VulnerabilityAssessmentResultResponse>(path, idUser, {
                headers,
            })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // vulnerability-assessment-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * addVulnerabilityAssessment
     */
    addVulnerabilityAssessment(
        vulnerabilityAssessment: FormData
    ): Observable<VulnerabilityAssessmentResponse> {
        let path = `${this._url}${this._vulnerability_assessment}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .post<VulnerabilityAssessmentResponse>(
                path,
                vulnerabilityAssessment,
                {
                    headers,
                }
            )
            .pipe(catchError(this.handleError));
    }

    /**
     * deleteVulnerabilityAssessment
     */
    deleteVulnerabilityAssessment(
        id: string
    ): Observable<VulnerabilityAssessmentResponse> {
        let path = `${this._url}${this._vulnerability_assessment}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .delete<VulnerabilityAssessmentResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * patchVulnerabilityAssessment
     */
    patchVulnerabilityAssessment(
        id: string,
        vulnerabilityAssessment: VulnerabilityAssessment
    ): Observable<VulnerabilityAssessmentResponse> {
        let path = `${this._url}${this._vulnerability_assessment}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .patch<VulnerabilityAssessmentResponse>(
                path,
                vulnerabilityAssessment,
                {
                    headers,
                }
            )
            .pipe(catchError(this.handleError));
    }

    /**
     * getVulnerabilityAssessment
     */
    getVulnerabilityAssessment(
        page: Number,
        limit: Number,
        sortBy: string,
        sortOrder: string
    ): Observable<VulnerabilityAssessmentResponse> {
        let path = `${this._url}${this._vulnerability_assessment}?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<VulnerabilityAssessmentResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getVulnerabilityAssessmentById
     */
    getVulnerabilityAssessmentById(
        id: string,
        internalUser: boolean = true
    ): Observable<VulnerabilityAssessmentResponse> {
        let path =
            `${this._url}${this._vulnerability_assessment}/${id}` +
            (internalUser ? '' : '/user');
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<VulnerabilityAssessmentResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getVulnerabilityAssessmentByCompany
     */
    getVulnerabilityAssessmentByCompany(
        page: Number,
        limit: Number,
        sortBy: string,
        sortOrder: string
    ): Observable<VulnerabilityAssessmentResponse> {
        let path = `${this._url}${this._vulnerability_assessment}/company?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<VulnerabilityAssessmentResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getVulnerabilityAssessmentByCompanyUser
     */
    getVulnerabilityAssessmentByCompanyUser(
        page: Number,
        limit: Number,
        sortBy: string,
        sortOrder: string
    ): Observable<VulnerabilityAssessmentResponse> {
        let path = `${this._url}${this._vulnerability_assessment}/companyUser?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<VulnerabilityAssessmentResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getVulnerabilityAssessmentByBranchOffice
     */
    getVulnerabilityAssessmentByBranchOffice(
        page: Number,
        limit: Number,
        sortBy: string,
        sortOrder: string
    ): Observable<VulnerabilityAssessmentResponse> {
        let path = `${this._url}${this._vulnerability_assessment}/branchOffice?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<VulnerabilityAssessmentResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // role-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * getRole
     */
    getRole(): Observable<RoleResponse> {
        let path = `${this._url}${this._role}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<RoleResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * addRole
     */
    addRole(role: Role): Observable<RoleResponse> {
        let path = `${this._url}${this._role}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .post<RoleResponse>(path, role, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getRoleById
     */
    getRoleById(id: string): Observable<RoleResponse> {
        let path = `${this._url}${this._role}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<RoleResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // internal-user-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * getInternalUser
     */
    getInternalUser(): Observable<InternalUserResponse> {
        let path = `${this._url}${this._internal_user}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<InternalUserResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * addInternalUser
     */
    addInternalUser(user: InternalUser): Observable<InternalUserResponse> {
        let path = `${this._url}${this._internal_user}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .post<InternalUserResponse>(path, user, {
                headers,
            })
            .pipe(catchError(this.handleError));
    }

    /**
     * getInternalUserById
     */
    getInternalUserById(id: string): Observable<InternalUserResponse> {
        let path = `${this._url}${this._internal_user}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<InternalUserResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // branch-office-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * getBranchOffice
     */
    getBranchOffice(
        page: Number,
        limit: Number,
        sortBy: string,
        sortOrder: string
    ): Observable<BranchOfficeResponse> {
        let path = `${this._url}${this._branch_office}?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<BranchOfficeResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getBranchOfficeByCompany
     */
    getBranchOfficeByCompany(
        page: Number,
        limit: Number,
        sortBy: string,
        sortOrder: string
    ): Observable<BranchOfficeResponse> {
        let path = `${this._url}${this._branch_office}/company?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<BranchOfficeResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getBranchOfficeByCompanyId
     */
    getBranchOfficeByCompanyId(
        companyId: string,
        page: Number,
        limit: Number,
        sortBy: string,
        sortOrder: string
    ): Observable<BranchOfficeResponse> {
        let path = `${this._url}${this._branch_office}/company/${companyId}?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<BranchOfficeResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * deleteBranchOffice
     */
    deleteBranchOffice(id: string): Observable<BranchOfficeResponse> {
        let path = `${this._url}${this._branch_office}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .delete<BranchOfficeResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * addBranchOffice
     */
    addBranchOffice(
        branchOffice: BranchOffice
    ): Observable<BranchOfficeResponse> {
        let path = `${this._url}${this._branch_office}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .post<BranchOfficeResponse>(path, branchOffice, {
                headers,
            })
            .pipe(catchError(this.handleError));
    }

    /**
     * getBranchOfficeById
     */
    getBranchOfficeById(id: string): Observable<BranchOfficeResponse> {
        let path = `${this._url}${this._branch_office}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<BranchOfficeResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * updateBranchOffice
     */
    updateBranchOffice(
        id: string,
        branchOffice: BranchOffice
    ): Observable<BranchOfficeResponse> {
        let path = `${this._url}${this._branch_office}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .put<BranchOfficeResponse>(path, branchOffice, {
                headers,
            })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // brand-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * getBrand
     */
    getBrand(
        page: Number,
        limit: Number,
        sortBy: string,
        sortOrder: string
    ): Observable<BrandResponse> {
        let path = `${this._url}${this._brand}?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<BrandResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * deleteBrand
     */
    deleteBrand(id: string): Observable<BrandResponse> {
        let path = `${this._url}${this._brand}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .delete<BrandResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * addBrand
     */
    addBrand(brand: Brand): Observable<BrandResponse> {
        let path = `${this._url}${this._brand}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .post<BrandResponse>(path, brand, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getBrandByCompanyId
     */
    getBrandByCompanyId(
        companyId: string,
        page: Number,
        limit: Number,
        sortBy: string,
        sortOrder: string
    ): Observable<BrandResponse> {
        let path = `${this._url}${this._brand}/company/${companyId}?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<BrandResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getBrandById
     */
    getBrandById(id: string): Observable<BrandResponse> {
        let path = `${this._url}${this._brand}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<BrandResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * updateBrand
     */
    updateBrand(id: string, brand: Brand): Observable<BrandResponse> {
        let path = `${this._url}${this._brand}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .put<BrandResponse>(path, brand, { headers })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // asset-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * getAssets
     */
    getAssets(
        page: Number,
        limit: Number,
        sortBy: string,
        sortOrder: string
    ): Observable<AssetResponse> {
        let path = `${this._url}${this._asset}?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<AssetResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getAssetById
     */
    getAssetById(id: string): Observable<AssetResponse> {
        let path = `${this._url}${this._asset}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<AssetResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getAssetsByBranchOficceId
     */
    getAssetsByBranchOficceId(
        branchOfficeId: string,
        pattern: string
    ): Observable<AssetResponse> {
        let path = `${this._url}${this._asset}/autocomplete/branchOffice/${branchOfficeId}?pattern=${pattern}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<AssetResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getAssetsByPattern
     */
    getAssetsByPattern(pattern: string): Observable<AssetResponse> {
        let path = `${this._url}${this._asset}/autocomplete/branchOffice?pattern=${pattern}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<AssetResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * addAsset
     */
    addAsset(asset: Asset): Observable<AssetResponse> {
        let path = `${this._url}${this._asset}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .post<AssetResponse>(path, asset, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * deleteAsset
     */
    deleteAsset(id: string): Observable<AssetResponse> {
        let path = `${this._url}${this._asset}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .delete<AssetResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * updateAsset
     */
    updateAsset(id: string, asset: Asset): Observable<AssetResponse> {
        let path = `${this._url}${this._asset}/${id}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .put<AssetResponse>(path, asset, { headers })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // vulnerability-tool-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * getVulnerabilityTool
     */
    getVulnerabilityTool(): Observable<VulnerabilityToolResponse> {
        let path = `${this._url}${this._vulnerability_tool}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<VulnerabilityToolResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // vulnerability-origin-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * getVulnerabilityOrigin
     */
    getVulnerabilityOrigin(): Observable<VulnerabilityOriginResponse> {
        let path = `${this._url}${this._vulnerability_origin}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<VulnerabilityOriginResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // state-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * getState
     */
    getState(): Observable<StateResponse> {
        let path = `${this._url}${this._state}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<StateResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getStateDestination
     */
    getStateDestination(state: string): Observable<StateResponse> {
        let path = `${this._url}${this._state}/destination/${state}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<StateResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // severity-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * getSeverity
     */
    getSeverity(): Observable<SeverityResponse> {
        let path = `${this._url}${this._severity}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<SeverityResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // contact-medium-type-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * getContactMediumType
     */
    getContactMediumType(): Observable<ContactMediumTypeResponse> {
        let path = `${this._url}${this._contact_medium_type}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<ContactMediumTypeResponse>(path, {
                headers,
            })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // asset-verification-status-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * getAssetVerificationStatus
     */
    getAssetVerificationStatus(): Observable<AssetVerificationStatusResponse> {
        let path = `${this._url}${this._asset_verification_status}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<AssetVerificationStatusResponse>(path, {
                headers,
            })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // health-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * health
     */
    health(): Observable<any> {
        let path = `${this._url}${this._health}`;
        return this._httpClient.get(path).pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // dashboard-chart-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * getDashboardByChart
     */
    getDashboardByChart(chart: string): Observable<DashboardResponse> {
        let path = `${this._url}${this._dashboard}/${chart}/companyUser`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<DashboardResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getDashboardByBranchOficceId
     */
    getDashboardByBranchOficceId(
        chart: string,
        branchOfficeId: string,
        internalUser: boolean = true
    ): Observable<DashboardResponse> {
        let path =
            `${this._url}${this._dashboard}/${chart}/BranchOffice/${branchOfficeId}` +
            (internalUser ? '' : '/company');
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<DashboardResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // vulnerability-manager-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * getCompanyUsersByVulnerabilityAssessmentId
     */
    getCompanyUsersByVulnerabilityAssessmentId(
        vulnerabilityAssessmentId: string,
        role: string
    ): Observable<VulnerabilityManagerResponse> {
        let path =
            `${this._url}${this._vulnerability_manager}/vulnerabilityAssessment/${vulnerabilityAssessmentId}` +
            (role == GlobalConstants.supervisor ? '/manager' : '');
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<VulnerabilityManagerResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // vulnerability-report-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * getVulnerabilityReport
     */
    getVulnerabilityReport(
        reportType: string,
        companyId: string
    ): Observable<Blob> {
        let path =
            `${this._url}${this._vulnerability_report}` +
            (companyId ? `/${companyId}` : ``) +
            `?reportType=${reportType}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .get<Blob>(path, {headers: headers, responseType: 'blob' as 'json'})
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // logout-controller-impl
    // -----------------------------------------------------------------------------------------------------

    /**
     * kill
     */
    kill(): Observable<AuthenticationResponse> {
        let path = `${this._url}${this._kill}`;
        const headers = new HttpHeaders({
            //Authorization: 'Bearer ' + this._authService.accessToken,
        });
        return this._httpClient
            .post<AuthenticationResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // handleError
    // -----------------------------------------------------------------------------------------------------

    handleError(error: any) {
        // This method is invoked by RxJS catchError; the error object may
        // differ depending on where it originated (HttpErrorResponse, our
        // custom error messages, etc.).  Guard against null/undefined and
        // prefer a human readable message when available.
        console.error(error);

        let message = 'An unknown error occurred';
        if (error) {
            if (typeof error === 'string') {
                message = error;
            } else if (error.error && typeof error.error === 'string') {
                message = error.error;
            } else if (error.message) {
                message = error.message;
            }
        }

        return throwError(() => new Error(message));
    }

    // -----------------------------------------------------------------------------------------------------
    // loadContent
    // -----------------------------------------------------------------------------------------------------

    async loadContent(route: string): Promise<string> {
        try {
            const response = await fetch(`assets/html/${route}`);
            if (!response.ok) {
                throw new Error(
                    `Error to load ${route}: ${response.statusText}`
                );
            }
            return await response.text();
        } catch (error) {
            console.error(error);
            return '';
        }
    }
}
